import { Fragment, useState } from 'react'
import { ActionInfo, Button, ResourceSelectField, SelectField, StatusMessage, TextField } from '@krp/ui-kit'
import { sourceOptions, type SourceKinds } from '../../../shared/sourceOptions'
import type { CashierCandidate, CashierCandidates } from '../../../shared/candidates'
import type { CashierAudit, CashierDirectory, CashierLinksDraft, CashierMember } from '../model/types'
import './links.css'

/** Кассиры одного источника: экран сам не ходит в сеть, строки пары просят их у привязки. */
export interface CashierSourceCandidates { candidates: CashierCandidates | undefined; loading: boolean }
export interface CashierLinksProps {
  directory: CashierDirectory | undefined
  /** Вид источника: метка (как на плитке), цвет и общий порядок источников; без него вид не показывается. */
  kinds?: SourceKinds
  draft: CashierLinksDraft | null
  month: string
  audit: CashierAudit[] | undefined
  busy: boolean
  loading: boolean
  error: string | null
  saved: boolean
  /** Кассиры выбранного источника за месяц; вызывается как хук из строки пары. */
  useCandidates: (resourceId: string) => CashierSourceCandidates
  remove: (id: string) => void
  choosePerson: (id: string) => void
  patch: (patch: Partial<CashierLinksDraft>) => void
  period: (value: string) => void
  save: () => void
  reload: () => void
}
function auditDescription(json: string) {
  try {
    const value: unknown = JSON.parse(json)
    if (typeof value !== 'object' || value === null) return 'Карточка ещё не создана'
    const item = value as Record<string, unknown>
    const members = Array.isArray(item.members) ? item.members as { resourceName?: string; name?: string }[] : []
    return `${typeof item.name === 'string' ? item.name : ''}${item.archived === true ? ' (архив)' : ''}: ${members.map((member) => `${member.resourceName ?? ''} — ${member.name ?? ''}`).join('; ')}`
  } catch { return 'История недоступна' }
}
const memberId = (member: { resourceId: string; key: string }) => `${member.resourceId}:${member.key}`
function toMember(item: CashierCandidate, source: { id: string; name: string }, month: string): CashierMember {
  return { resourceId: source.id, resourceName: source.name, ...(item.sourceCashierId == null ? {} : { sourceCashierId: item.sourceCashierId }), ...(item.cashierId == null ? {} : { cashierId: item.cashierId }), key: item.key, name: item.name, observedMonth: month }
}

/** Одна пара «источник — кассир»: выбрали источник, подгрузились его кассиры, выбрали кассира. */
interface PairRow { id: number; resourceId: string; member: CashierMember | null }
const MIN_ROWS = 2
/** Номер строки пары: нужен только как уникальный ключ списка, поэтому счётчик общий на модуль. */
let lastRowId = 0
const nextRowId = (): number => {
  lastRowId += 1
  return lastRowId
}

function PairRowEditor(props: {
  row: PairRow; index: number; draftId: string; month: string; sources: CashierDirectory['sources']; kinds: CashierLinksProps['kinds']; sourcesLoading: boolean; taken: Set<string>; canRemove: boolean
  useCandidates: CashierLinksProps['useCandidates']; change: (row: PairRow) => void; remove: () => void
}) {
  const { row, index } = props
  const { candidates, loading } = props.useCandidates(row.resourceId)
  const source = props.sources.find((item) => item.id === row.resourceId)
  const items = candidates?.items ?? []
  const free = items.filter((item) => !props.taken.has(memberId({ resourceId: row.resourceId, key: item.key })) && (item.personId === null || item.personId === props.draftId))
  const hidden = items.filter((item) => item.personId !== null && item.personId !== props.draftId && item.key !== row.member?.key).length
  const options = [{ value: '', label: row.resourceId === '' ? 'Сначала выберите источник' : loading ? 'Загружаем кассиров…' : 'Выберите кассира' },
    ...(row.member !== null && !free.some((item) => item.key === row.member?.key) ? [{ value: row.member.key, label: row.member.name }] : []),
    ...free.map((item) => ({ value: item.key, label: `${item.name}${item.count > 0 ? ` · ${item.count} чеков` : ''}${item.ambiguous === true ? ' · тёзки' : ''}` }))]
  const picked = items.find((item) => item.key === row.member?.key)
  return <li className="cashier-links__pair">
    <span className="cashier-links__ordinal" aria-hidden="true">{index + 1}</span>
    <ResourceSelectField label={`Источник ${index + 1}`} actionId="receipts.cashier-groups.choose" value={row.resourceId}
      options={sourceOptions(props.sources, props.kinds)} notChosen="Выберите источник" loading={props.sourcesLoading}
      onChange={(resourceId) => props.change({ ...row, resourceId, member: null })} />
    <SelectField label={`Кассир ${index + 1}`} actionId="receipts.cashier-groups.choose" value={row.member?.key ?? ''} disabled={row.resourceId === '' || source === undefined}
      options={options} onChange={(key) => {
        const item = items.find((entry) => entry.key === key)
        props.change({ ...row, member: item === undefined || source === undefined ? null : toMember(item, source, props.month) })
      }} />
    <Button actionId="receipts.cashier-groups.remove" disabled={!props.canRemove} onClick={props.remove}>Убрать<span className="cashier-links__sr-only"> пару {index + 1}</span></Button>
    <div className="cashier-links__pair-note">
      {loading && row.resourceId !== '' && <p role="status">Загружаем кассиров…</p>}
      {!loading && row.resourceId !== '' && candidates !== undefined && items.length === 0 && <p>В источнике нет кассиров за выбранный месяц. Другой месяц — в «Дополнительно».</p>}
      {hidden > 0 && <p>Скрыто кассиров, уже состоящих в других связях: {hidden}.</p>}
      {picked?.ambiguous === true && <p>В источнике есть тёзки: их неразличимые продажи будут объединены.</p>}
      {row.member !== null && (row.member.key.startsWith('name:') || picked?.identityStrength === 'exact-name') && <p>Связь по точному имени. При смене имени добавьте новое написание.</p>}
    </div>
  </li>
}

function LinkEditor(props: CashierLinksProps & { draft: CashierLinksDraft }) {
  const { draft } = props
  const sources = props.directory?.sources ?? []
  const nextId = nextRowId
  const [rows, setRows] = useState<PairRow[]>(() => {
    const saved = draft.members.map((member) => ({ id: nextId(), resourceId: member.resourceId, member }))
    return [...saved, ...Array.from({ length: Math.max(0, MIN_ROWS - saved.length) }, () => ({ id: nextId(), resourceId: '', member: null }))]
  })
  const commit = (next: PairRow[]) => {
    setRows(next)
    const members = next.flatMap((row) => row.member === null ? [] : [row.member])
    props.patch({ members, ...(draft.name.trim() === '' && members[0] !== undefined ? { name: members[0].name } : {}) })
  }
  const taken = (row: PairRow) => new Set(rows.flatMap((other) => other.id === row.id || other.member === null ? [] : [memberId(other.member)]))
  const incomplete = rows.some((row) => row.resourceId !== '' && row.member === null)
  const enoughSources = new Set(draft.members.map((member) => member.resourceId)).size >= 2
  return <fieldset disabled={props.busy}>
    <p>Выберите, какие записи кассиров из разных источников — один человек. В каждой строке: источник, затем кассир этого источника.</p>
    <ul className="cashier-links__pairs" aria-label="Кассиры связи">{rows.map((row, index) => <PairRowEditor key={row.id} row={row} index={index} draftId={draft.id} month={props.month}
      sources={sources} kinds={props.kinds} sourcesLoading={props.loading && props.directory === undefined} taken={taken(row)} canRemove={rows.length > MIN_ROWS} useCandidates={props.useCandidates}
      change={(changed) => commit(rows.map((item) => item.id === changed.id ? changed : item))}
      remove={() => commit(rows.filter((item) => item.id !== row.id))} />)}</ul>
    <div className="cashier-links__toolbar">
      <Button actionId="receipts.cashier-groups.add" onClick={() => setRows([...rows, { id: nextId(), resourceId: '', member: null }])}>+ Источник</Button>
    </div>
    <TextField label="Имя в отчётах" actionId="receipts.cashier-groups.choose" value={draft.name} onChange={(name) => props.patch({ name })} />
    <ActionInfo id="receipts.cashier-groups.audit" layout="block"><details><summary>Дополнительно</summary>
      <label>Месяц, за который берутся кассиры из чеков <input type="month" value={props.month.slice(0, 7)} onChange={(event) => props.period(`${event.target.value}-01`)} /></label>
      {draft.version > 0 && <><h4>История изменений</h4><ol>{(props.audit ?? []).map((item) => <li key={item.operationId}>
        <p>{new Date(item.at).toLocaleString('ru-RU')} · {item.actor}</p><p>Было: {auditDescription(item.before)}</p><p>Стало: {auditDescription(item.after)}</p>
      </li>)}</ol></>}
    </details></ActionInfo>
    {incomplete && <p>В строке выбран источник, но не кассир: выберите кассира или уберите строку.</p>}
    {!enoughSources && <p>Нужны кассиры минимум из двух разных источников.</p>}
    <div className="cashier-links__toolbar">
      <Button variant="primary" actionId="receipts.cashier-groups.save" busy={props.busy} disabled={draft.name.trim() === '' || !enoughSources || incomplete} onClick={props.save}>Сохранить связь</Button>
      <Button actionId="receipts.cashier-groups.choose" disabled={props.busy} onClick={() => props.choosePerson('')}>Отмена</Button>
    </div>
  </fieldset>
}

export function CashierLinksScreen(props: CashierLinksProps) {
  const [removing, setRemoving] = useState<string | null>(null)
  const draft = props.draft
  const people = (props.directory?.people ?? []).filter((person) => !person.archived)
  return <section className="cashier-links" aria-label="Связи кассиров">
    {props.error !== null && <StatusMessage kind="error" title="Не удалось выполнить действие" text={props.error} />}
    {props.saved && <p role="status">Связи обновлены.</p>}
    {draft === null ? <>
      <div className="cashier-links__toolbar">
        <Button variant="primary" actionId="receipts.cashier-groups.new" disabled={props.directory?.canManage !== true || props.busy} onClick={() => props.choosePerson('new')}>Добавить связь</Button>
        <Button actionId="receipts.cashier-groups.reload" busy={props.loading} onClick={props.reload}>Обновить</Button>
      </div>
      {props.loading && props.directory === undefined && <p role="status">Загружаем связи…</p>}
      {props.directory !== undefined && <table className="cashier-links__table">
        <caption className="cashier-links__sr-only">Существующие связи</caption>
        <thead><tr><th scope="col">Человек</th><th scope="col">Кассиры в источниках</th><th scope="col"><span className="cashier-links__sr-only">Действия</span></th></tr></thead>
        <tbody>
          {people.length === 0 && <tr><td colSpan={3} className="cashier-links__empty">Связей пока нет.</td></tr>}
          {people.map((person) => <Fragment key={person.id}>
            <tr>
              <th scope="row">{person.name}</th>
              <td><ul className="cashier-links__identities">{person.members.map((member) => <li key={memberId(member)}><strong>{member.name}</strong><span>{member.resourceName}</span></li>)}</ul></td>
              <td className="cashier-links__row-actions">
                <Button actionId="receipts.cashier-groups.choose" aria-label={`Изменить связь ${person.name}`} disabled={!person.canEdit || props.busy} onClick={() => props.choosePerson(person.id)}>Изменить<span className="cashier-links__sr-only"> связь {person.name}</span></Button>
                <Button actionId="receipts.cashier-groups.archive" aria-label={`Удалить связь ${person.name}`} disabled={!person.canEdit || props.busy} onClick={() => setRemoving(person.id)}>Удалить<span className="cashier-links__sr-only"> связь {person.name}</span></Button>
                {!person.canEdit && <small>Нужен доступ ко всем источникам связи.</small>}
              </td>
            </tr>
            {removing === person.id && <tr><td colSpan={3}><div className="cashier-links__confirmation" role="group" aria-label={`Удалить связь ${person.name}`}>
              <p>Удалить связь? Исходные кассиры и чеки сохранятся.</p>
              <Button variant="danger" actionId="receipts.cashier-groups.archive" busy={props.busy} onClick={() => props.remove(person.id)}>Подтвердить удаление</Button>
              <Button actionId="receipts.cashier-groups.choose" disabled={props.busy} onClick={() => setRemoving(null)}>Отмена</Button>
            </div></td></tr>}
          </Fragment>)}
        </tbody>
      </table>}
    </> : <>
      <div className="cashier-links__toolbar"><h3>{draft.version === 0 ? 'Новая связь' : 'Изменение связи'}</h3>
        <Button actionId="receipts.cashier-groups.choose" disabled={props.busy} onClick={() => props.choosePerson('')}>К списку связей</Button></div>
      <LinkEditor key={`${draft.id}:${draft.version}`} {...props} draft={draft} />
    </>}
  </section>
}
